import styled from "@emotion/styled"
import { useInView } from "react-intersection-observer"
import colors from "theme/colors"
import {
  Container,
  Desktop,
  Mobile,
  NewText,
  transitionDelayTime,
  TransitionUp,
  useInViewConfig,
  useTransitionUpStyles,
} from "ui"
import ImageWithAlt from "ui/ImageWithAlt"
import { delayTime, styleObjectToCss } from "ui/transitions/utils"
import { extractImage, extractProperty } from "utils"

const StyledProjectsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 24px;
  margin-top: 24px;
  margin-bottom: 112px;
  max-width: 100vw;
  overflow: scroll;
  padding-left: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    overflow: hidden;
    padding-left: 0px;
  }
`
const StyledProject = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--DarkGreen, #5b7553);
  min-width: 300px;
  max-width: 300px;
  ${(props) => props.theme.breakpoints.up("md")} {
    min-width: 331px;
    max-width: 331px;
  }
`

const StyledImageContainer = styled.div`
  height: 161px;
  width: 100%;
  position: relative;
`

const StyledTextContainer = styled.div`
  padding: 20px;
`

const StyledLinkContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
`

type Props = {
  data: any
}

function BonitaProjects({ data }: Props) {
  const blockTitle = extractProperty(data, "blockTitle")
  const shortDesDescription = extractProperty(data, "shortDesDescription")
  const projectBlock = extractProperty(data, "projectBlock")
  const { ref: mobileRef, inView: mobileInView } = useInView(useInViewConfig)
  const imageTransitionStyles = useTransitionUpStyles(mobileInView)
  const descriptionTransitionStyles = useTransitionUpStyles(mobileInView, delayTime)
  return (
    <>
      <Container>
        <NewText
          cssString={styleObjectToCss(imageTransitionStyles)}
          component={"h2"}
          desktopFontSize={40}
          desktopLineHeight={"44px"}
          desktopFontWeight={400}
          mobileFontSize={32}
          mobileLineHeight={"46px"}
          color={colors.black}
        >
          {blockTitle}
        </NewText>
        <NewText
          cssString={styleObjectToCss(descriptionTransitionStyles)}
          marginTop="24px"
          component={"p"}
          desktopFontSize={16}
          desktopLineHeight={"23px"}
          desktopFontWeight={400}
          mobileFontSize={14}
          mobileLineHeight={"20px"}
          color={colors.black}
        >
          {shortDesDescription}
        </NewText>
      </Container>
      <Container withoutPadding>
        <StyledProjectsContainer ref={mobileRef}>
          {projectBlock?.map((project: any, index: number) => {
            const desktopImage = extractImage(project, "imageForDesktop")
            const mobileImage = extractImage(project, "imageForMobile")
            const nameOfTheProject = extractProperty(project, "nameOfTheProject")
            const location = extractProperty(project, "location")
            const size = extractProperty(project, "size")
            const projectCapacity = extractProperty(project, "projectCapacity")
            const production = extractProperty(project, "production")
            const websiteLink = extractProperty(project, "websiteLink")
            return (
              <TransitionUp
                key={index.toString()}
                $inView={mobileInView}
                $delay={transitionDelayTime * 0.5 * index}
              >
                <StyledProject>
                  <StyledImageContainer>
                    <Desktop>
                      <ImageWithAlt image={desktopImage} fill />
                    </Desktop>
                    <Mobile>
                      <ImageWithAlt image={mobileImage} fill />
                    </Mobile>
                  </StyledImageContainer>
                  <StyledTextContainer>
                    <NewText
                      component={"h3"}
                      desktopFontSize={16}
                      desktopLineHeight={"23px"}
                      desktopFontWeight={600}
                      color={colors.black}
                    >
                      {nameOfTheProject}
                    </NewText>
                    <NewText
                      component={"p"}
                      desktopFontSize={14}
                      desktopLineHeight={"normal"}
                      desktopFontWeight={600}
                      color={colors.black}
                    >
                      Location: <span style={{ fontWeight: "400" }}>{location}</span>
                    </NewText>
                    <NewText
                      component={"p"}
                      desktopFontSize={14}
                      desktopLineHeight={"normal"}
                      desktopFontWeight={600}
                      color={colors.black}
                    >
                      Size: <span style={{ fontWeight: "400" }}>{size}</span>
                    </NewText>
                    <NewText
                      component={"p"}
                      desktopFontSize={14}
                      desktopLineHeight={"normal"}
                      desktopFontWeight={600}
                      color={colors.black}
                    >
                      Project capacity: <span style={{ fontWeight: "400" }}>{projectCapacity}</span>
                    </NewText>
                    <NewText
                      component={"p"}
                      desktopFontSize={14}
                      desktopLineHeight={"normal"}
                      desktopFontWeight={600}
                      color={colors.black}
                    >
                      Production: <span style={{ fontWeight: "400" }}>{production}</span>
                    </NewText>
                    <StyledLinkContainer>
                      <a
                        target="_blank"
                        style={{ textDecorationColor: colors.darkGreen }}
                        href={websiteLink?.[0]?.url}
                      >
                        <NewText
                          component={"p"}
                          desktopFontSize={14}
                          desktopLineHeight={"normal"}
                          desktopFontWeight={600}
                          color={colors.darkGreen}
                        >
                          {websiteLink?.[0]?.name}
                        </NewText>
                      </a>
                    </StyledLinkContainer>
                  </StyledTextContainer>
                </StyledProject>
              </TransitionUp>
            )
          })}
        </StyledProjectsContainer>
      </Container>
    </>
  )
}

export default BonitaProjects
